
import { colors, colors2 } from "oolib";
import React, { useEffect, useRef, useState } from "react";
import { StyledContentSection, StyledGridContainer, StyledLSBSection, StyledLSBToggleButton, StyledRSBSection, StyledRSBToggleButton } from "./styled";

/**
 * a little note about the props lsbAllowCollapse & rsbAllowCollapse:
 * the only purpose of this prop is to show the button that 
 * allows user to toggle the open/close state of the sidebar. 
 * 
 * But if you intent to control this manually from outside 
 * the comp, then you dont need to enable this prop
 */

export const SidebarLayoutV2 = ({
  gridHeightCSS = 'calc(100vh - 10rem)',
  // Apply oolib's globalScrollbarStyling to the centre content section.
  contentScrollbarStyling = false,
  toggleState : toggleStateFromParent,
  setToggleState : setToggleStateFromParent,
  toggleAnimationStyle = 'slide', //alt = mask
  
  lsbId,
  lsbWidth : _lsbWidth = 250,
  lsbClosedWidth = 0,
  lsbAllowCollapse = false,
  lsbAllowResize = false,
  // When true the edge toggle button only renders while the lsb is closed.
  // Use this when the sidebar's own header provides an in-panel collapse
  // affordance, so the edge button only re-appears as an "expand" handle.
  lsbShowToggleOnlyWhenClosed = false,
  lsbComp,

  rsbId,
  rsbWidth : _rsbWidth = 250,
  rsbClosedWidth = 0,
  rsbAllowCollapse = false,
  rsbAllowResize = false,
  rsbShowToggleOnlyWhenClosed = false,
  rsbComp,

  children,
  contentWidth, // "fit" - Takes only available space for children
  contentSectionMinWidth = '50vw',
}) => {

  const [lsbWidth, setLsbWidth ] = useState(_lsbWidth)
  const [rsbWidth, setRsbWidth ] = useState(_rsbWidth)

  //--- sidebar toggle handling ---- //
  //if comes from parent, then it controls it
  const [toggleState, setToggleState] = useState(
    toggleStateFromParent ||
    { lsb: true, rsb: true }
  )

  const handleSetToggleState = value => {
    let fireStateSetter = setToggleStateFromParent || setToggleState
    fireStateSetter(value)
  }
  
  const isFirstMount = useRef(true)
  
  useEffect(() => {
    console.log({toggleStateFromParent})
    if(!isFirstMount.current) setToggleState(toggleStateFromParent)
  },[toggleStateFromParent])

  useEffect(() => { isFirstMount.current = false },[])
  //--- end sidebar toggle handling ---- //

  const initResizeSidebarIsActiveState = {
    isActive: false,
    lsb_rsb: undefined,
    initClientX: undefined
  }
  const [resizeSidebarIsActive, setResizeSidebarIsActive] = useState(initResizeSidebarIsActiveState);

  // Refs to sibling overlay divs that render the resize-handle indicator
  // (the 2px line at the section's edge that highlights blue when the user
  // hovers in the resizeable zone).
  //
  // We can't paint the indicator on the sections themselves (their children
  // have opaque backgrounds that cover inset shadows / outlines), and we
  // can't render absolutely-positioned overlays INSIDE the sections either
  // (their overflow: hidden clips them). Siblings inside the relatively-
  // positioned grid container, with high z-index, work around both.
  const lsbResizeHandleIndicatorRef = useRef(null);
  const rsbResizeHandleIndicatorRef = useRef(null);

  const attachResizeHandleHandlers = (lsb_rsb) => {
    const thresholdCondition = e =>
      // Ignore events that bubble in from portaled content (e.g. lightboxes/
      // popovers rendered via FloatingPortal). React synthetic events propagate
      // through the React tree even when the DOM node lives outside the sidebar,
      // which would otherwise arm a resize / paint the indicator while the user
      // is interacting with a popover anchored inside the sidebar.
      e.currentTarget.contains(e.target) && (
        lsb_rsb === 'lsb'
          ? (
            e.clientX >= e.currentTarget.getBoundingClientRect().right - 10 &&
            e.clientX <= e.currentTarget.getBoundingClientRect().right //need this only because the toggle button is sticking out to the right of the sidebar
          )
          : (
            e.clientX <= e.currentTarget.getBoundingClientRect().left + 10 &&
            e.clientX >= e.currentTarget.getBoundingClientRect().left
          )
      )
    // The section's own border stays at 1px untouched (avoids the 1-px
    // content-shift the old border-swap caused). Hover/active state paints
    // a sibling overlay div positioned at the section's edge — see
    // lsbResizeHandleIndicatorRef / rsbResizeHandleIndicatorRef.
    const resizeHandleIndicatorRef =lsb_rsb === 'lsb' ? lsbResizeHandleIndicatorRef : rsbResizeHandleIndicatorRef;
    return ({
    onMouseMove: e => {
      if(thresholdCondition(e)){
        if (resizeHandleIndicatorRef.current) resizeHandleIndicatorRef.current.style.background = colors2.blue;
        e.currentTarget.style.cursor = 'ew-resize';
      }else{
        if (resizeHandleIndicatorRef.current) resizeHandleIndicatorRef.current.style.background = 'transparent';
        e.currentTarget.style.cursor = 'unset';
      }
    },
    onMouseOut: e => {
      if (resizeHandleIndicatorRef.current) resizeHandleIndicatorRef.current.style.background = 'transparent';
      e.currentTarget.style.cursor = 'unset';
    },
    onMouseDown: e => {
      if(thresholdCondition(e)){
        setResizeSidebarIsActive({
          isActive: true,
          lsb_rsb,
          initClientX: e.clientX,
          initWidth: lsb_rsb === 'lsb' ? lsbWidth : rsbWidth
        })

      }
    }
  })}

  return (
    <StyledGridContainer
      {...{
        gridHeightCSS,
        toggleState,
        lsbComp,
        rsbComp,
        lsbWidth,
        lsbClosedWidth,
        rsbWidth,
        rsbClosedWidth,
        resizeSidebarIsActive,
      }}
      onMouseUp={(e) => {
        if (resizeSidebarIsActive.isActive) {
          setResizeSidebarIsActive(initResizeSidebarIsActiveState);
        }
      }}
      onMouseMove={(e) => {
        if (resizeSidebarIsActive.isActive) {
          const { lsb_rsb, initClientX, initWidth } = resizeSidebarIsActive;
          if (lsb_rsb === "lsb") {
            setLsbWidth(initWidth + (e.clientX - initClientX));
          } else {
            setRsbWidth(initWidth - (e.clientX - initClientX));
          }
        }
      }}
    >
      {lsbComp && (
        <StyledLSBSection
          {...(lsbAllowResize ? attachResizeHandleHandlers("lsb") : {})}
          {...{ lsbWidth, toggleAnimationStyle }}
        >
          {lsbAllowCollapse && (!lsbShowToggleOnlyWhenClosed || !toggleState.lsb) && <StyledLSBToggleButton
            lsbWidth={lsbWidth}
            lsbClosedWidth={lsbClosedWidth}
            toggleState={toggleState}
            resizeSidebarIsActive={resizeSidebarIsActive}
            icon={toggleState.lsb ? 'CaretLeft': 'CaretRight'}
            onClick={() => handleSetToggleState(prev => ({...prev, lsb: prev.lsb ? false : true}))}
            />}
          <div style={{direction: 'ltr', height: '100%'}}>{lsbComp}</div>
        </StyledLSBSection>
      )}
      <StyledContentSection $scrollbar={contentScrollbarStyling} style={{ background: colors.white, overflow: 'auto' }}>
        <div style={{minWidth: contentWidth === "fit" ? '100%' : contentSectionMinWidth, height: '100%'}}>
          {children}
        </div>
      </StyledContentSection>
      {rsbComp && (
        <StyledRSBSection
          {...(rsbAllowResize ? attachResizeHandleHandlers("rsb") : {})}
          {...{ rsbWidth, toggleAnimationStyle }}
        >
          {rsbAllowCollapse && (!rsbShowToggleOnlyWhenClosed || !toggleState.rsb) && <StyledRSBToggleButton
            rsbWidth={rsbWidth}
            rsbClosedWidth={rsbClosedWidth}
            toggleState={toggleState}
            resizeSidebarIsActive={resizeSidebarIsActive}
            icon={toggleState.rsb ? 'CaretRight': 'CaretLeft'}
            onClick={() => handleSetToggleState(prev => ({...prev, rsb: prev.rsb ? false : true}))}
            />}
          {rsbComp}
        </StyledRSBSection>
      )}
      {/* Resize-handle indicator overlays — siblings of the sections inside the
          relatively-positioned grid container. High z-index so they render on
          top of any section content / borders. pointer-events: none so they
          don't interfere with the sections' own mouse handlers. */}
      {lsbComp && lsbAllowResize && toggleState.lsb && (
        <div
          ref={lsbResizeHandleIndicatorRef}
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: `${lsbWidth - 2}px`,
            width: '2px',
            background: 'transparent',
            pointerEvents: 'none',
            zIndex: 1200,
            ...(resizeSidebarIsActive.isActive ? {} : { transition: 'left 0.3s cubic-bezier(0.25, 1, 0.4, 1), background 0.1s' }),
          }}
        />
      )}
      {rsbComp && rsbAllowResize && toggleState.rsb && (
        <div
          ref={rsbResizeHandleIndicatorRef}
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            right: `${rsbWidth - 2}px`,
            width: '2px',
            background: 'transparent',
            pointerEvents: 'none',
            zIndex: 1200,
            ...(resizeSidebarIsActive.isActive ? {} : { transition: 'right 0.3s cubic-bezier(0.25, 1, 0.4, 1), background 0.1s' }),
          }}
        />
      )}
    </StyledGridContainer>
  );
};
